import { Flex, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import styles from '@/components/custom-filters/custom-filters.module.scss';
import { DashboardFilters } from '@/services/dashboard-service/dashboard.service.types.ts';
import { CallLogFilters } from '@/services/call-log-service/call-log-service.types';
import { useEffect, useMemo, useRef } from 'react';
import { debounce } from '@/utils/debounce/debounce';
import { SEARCH_DELAY } from '@/hooks/use-debounced-filter-query/use-debounced-filter-query';
import clsx from 'clsx';

interface CustomFiltersProps {
  onChangeFilters: (filters: DashboardFilters | CallLogFilters) => void;
  filters: DashboardFilters | CallLogFilters;
  companiesList: string[];
  defaultCompanyValue?: string;
  onSearchQueryChange: (query: string) => void;
  searchResults?: string[];
  isSearchLoading?: boolean;
  searchError?: Error | null;
  customClassName?: string;
  /** Lets a caller bind its own <label> to the select. */
  id?: string;
}

export function CustomFilters({
  onChangeFilters,
  filters,
  companiesList,
  defaultCompanyValue,
  onSearchQueryChange,
  searchResults,
  isSearchLoading,
  searchError,
  customClassName,
  id,
}: CustomFiltersProps) {
  const { t } = useTranslation('common');
  // Read through a ref so a caller passing an inline arrow cannot orphan the pending timer.
  const latestSearchChange = useRef(onSearchQueryChange);
  useEffect(() => {
    latestSearchChange.current = onSearchQueryChange;
  });

  const debouncedSearchChange = useMemo(
    () => debounce((value: string) => latestSearchChange.current(value), SEARCH_DELAY),
    []
  );

  useEffect(() => debouncedSearchChange.cancel, [debouncedSearchChange]);

  const handleSearch = (value: string) => {
    debouncedSearchChange(value);
  };

  const handleCompanyChange = (value: string | null) => {
    onChangeFilters({
      ...filters,
      company_name: value ?? defaultCompanyValue,
    });
    onSearchQueryChange('');
  };

  const allCompanies = searchResults && searchResults.length > 0 ? searchResults : companiesList;

  const uniqueCompanies = [...new Set(allCompanies)];

  const mappedCompaniesList = [
    ...uniqueCompanies.map((company) => ({
      value: company,
      label: company,
    })),
  ];

  // Nothing to switch with exactly one company, so the control is not rendered. An empty
  // list means the request failed or has not landed, and hiding on that would remove the
  // only recovery. Counted from the caller's list rather than a query of our own: a second
  // observer on the companies query refetches it, which re-scopes the page and re-runs
  // every metric query behind it.
  if (companiesList.length === 1) return null;

  return (
    <Flex>
      <Select
        className={clsx(styles.select, customClassName)}
        classNames={{
          popup: {
            root: styles.dropdown,
          },
        }}
        id={id}
        allowClear={filters.company_name == defaultCompanyValue ? false : true}
        options={mappedCompaniesList}
        value={filters.company_name}
        defaultValue={defaultCompanyValue}
        showSearch
        onChange={handleCompanyChange}
        onSearch={handleSearch}
        placeholder={t('filters.company.placeholder')}
        loading={isSearchLoading}
        notFoundContent={
          isSearchLoading
            ? t('filters.company.searching')
            : searchError
              ? t('filters.company.searchUnavailable')
              : t('filters.company.notFound')
        }
      />
    </Flex>
  );
}
